import React, { useState, useEffect, useRef } from "react";
import { US_STATES } from "./USStates";
import { formatPhoneNumber } from "./PhoneFormatter";
import { validateForm, parseServerError } from "./FormValidation";
import FormField from "./FormField";

const CustomerForm = ({ onSubmit, onCancel, initialData = null, isLoading = false, error = null }) => {
  const [formData, setFormData] = useState(
    initialData || {
      name: "",
      username: "",
      phone: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zip: "",
      paymentTerms: "",
      notes: "",
    }
  );

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const formRef = useRef(null);

  // Removed auto-scroll effect as it's not needed in modal

  // Parse server error and set specific field errors
  useEffect(() => {
    if (error) {
      const { errors: parsedErrors, serverError: parsedServerError } = parseServerError(error);
      setErrors(parsedErrors);
      setServerError(parsedServerError);
    } else {
      setErrors({});
      setServerError(null);
    }
  }, [error]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "zip" && !/^\d{0,5}$/.test(value)) return;
    if (name === "city" && /\d/.test(value)) return;

    let processedValue = value;
    
    // Format phone number
    if (name === "phone") {
      processedValue = formatPhoneNumber(value);
    }

    // Live validation for email and phone
    const newErrors = { ...errors };
    if (name === "username") {
      // Clear error when user starts typing
      if (newErrors[name]) {
        delete newErrors[name];
      }
      // Validate email format in real-time
      if (value && value.trim() !== "") {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.username = "Please enter a valid email address.";
        }
      }
    }
    
    if (name === "phone") {
      // Clear error when user starts typing
      if (newErrors[name]) {
        delete newErrors[name];
      }
      // Validate phone format in real-time
      if (processedValue && processedValue.trim() !== "") {
        const phoneDigits = processedValue.replace(/\D/g, "");
        if (phoneDigits.length > 0 && phoneDigits.length !== 10) {
          newErrors.phone = "Phone must be exactly 10 digits.";
        }
      }
    }

    // Clear field-specific error when user starts typing (for other fields)
    if (errors[name] && name !== "username" && name !== "phone") {
      delete newErrors[name];
    }

    setErrors(newErrors);
    setFormData({
      ...formData,
      [name]: processedValue,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = validateForm(formData);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    // Prepare submission data
    const submissionData = {
      ...formData,
    };

    onSubmit(submissionData);
  };

  return (
    <div ref={formRef}>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* Name */}
        <FormField
          label="Name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleInputChange}
          error={errors.name}
          required
          disabled={isLoading}
        />

        {/* Email (optional) */}
        <FormField
          label="Email"
          name="username"
          type="email"
          value={formData.username}
          onChange={handleInputChange}
          error={errors.username}
          disabled={isLoading}
        />

        {/* Phone (optional) */}
        <FormField
          label="Phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleInputChange}
          error={errors.phone}
          disabled={isLoading}
        />

        {/* Address Line 1 */}
        <FormField
          label="Address Line 1"
          name="addressLine1"
          type="text"
          value={formData.addressLine1}
          onChange={handleInputChange}
          required
          disabled={isLoading}
        />

        {/* Address Line 2 */}
        <FormField
          label="Address Line 2"
          name="addressLine2"
          type="text"
          value={formData.addressLine2}
          onChange={handleInputChange}
          disabled={isLoading}
        />

        {/* City, State, ZIP */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* City */}
          <FormField
            label="City"
            name="city"
            type="text"
            value={formData.city}
            onChange={handleInputChange}
            error={errors.city}
            required
            disabled={isLoading}
          />

          {/* State */}
          <FormField
            label="State"
            name="state"
            type="select"
            value={formData.state}
            onChange={handleInputChange}
            error={errors.state}
            required
            disabled={isLoading}
            options={US_STATES}
          />

          {/* ZIP */}
          <FormField
            label="ZIP"
            name="zip"
            type="text"
            value={formData.zip}
            onChange={handleInputChange}
            error={errors.zip}
            required
            disabled={isLoading}
          />
        </div>

        {/* Customer Settings Section */}
        <div className="border-t border-gray-200 pt-4 mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Customer Settings</h3>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              Optional
            </span>
          </div>

          {/* Payment Terms */}
          <div className="mb-4">
            <FormField
              label="Payment Terms"
              name="paymentTerms"
              type="select"
              value={formData.paymentTerms}
              onChange={handleInputChange}
              disabled={isLoading}
              options={[
                { value: "", label: "Due on Receipt (default)" },
                { value: "DUE_ON_RECEIPT", label: "Due on Receipt" },
                { value: "NET_15", label: "Net 15 Days" },
                { value: "NET_30", label: "Net 30 Days" },
                { value: "NET_60", label: "Net 60 Days" },
              ]}
            />
          </div>

          {/* Info about global pricing */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-blue-700">
              <strong>Note:</strong> Discount tiers are now configured globally in Account Settings and can be overridden per order. To set up volume discount tiers, go to Settings → Account Settings → Wholesale Pricing Tiers.
            </p>
          </div>

          {/* Notes */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="2"
              placeholder="Any special notes about this customer (e.g., preferred shipping method, special arrangements)..."
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Server-side error */}
        {serverError && (
          <div className="text-red-500 text-sm bg-red-50 p-3 rounded-md border border-red-200">
            {serverError}
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            type="submit"
            className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
            disabled={isLoading}
          >
            {isLoading ? (initialData ? "Updating..." : "Adding...") : (initialData ? "Update Customer" : "Add Customer")}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerForm;
